/**
 * Pure form-state mappers for the Check Organisation page. Lives outside the
 * page itself so the (largely orthogonal) wire-format conversion can be tested
 * and reasoned about independently of the React surface — the page reads as
 * orchestration, this file reads as plumbing.
 *
 * Trimmed copy of the TMS mapper: Check only edits the company-identity,
 * address and regional fields, so the invoice-sequence / additional-currency /
 * order-reference helpers are intentionally absent. The finance-only fields
 * (`currencyIsoCode`, `accountingSystem`, `vatRoundingMethod`, `extraFields`)
 * still live on the form so they round-trip **unchanged** through the
 * Organisation PUT — the page never renders them, but we must echo the server
 * values rather than drop them.
 *
 * Every helper here is a pure function: server DTO → form state, or form
 * state → wire request. No React, no toasts, no side effects.
 */
import type {
  Address,
  CreateAddressRequest,
  CreateSiteRequest,
  Organisation,
  UpdateAddressRequest,
  UpdateOrganisationRequest,
  AccountingSystemValue,
  VatRoundingMethodValue,
} from '@my/directory-api';
import type { ExtraField } from '@my/ui';

// ────────────────────────────────────────────────────────────────────────────
// Form types
// ────────────────────────────────────────────────────────────────────────────

export interface OrganisationForm {
  name: string;
  invoicingName: string;
  networkId: string;
  taxNumber: string;
  registrationNumber: string;
  operationalTimeZone: string;
  languageCode: string;
  timePattern: string;
  datePattern: string;
  // ── Not rendered on the Check page — preserved so the Organisation PUT
  // echoes the server values instead of clearing them. ──
  currencyIsoCode: string;
  accountingSystem: AccountingSystemValue;
  vatRoundingMethod: VatRoundingMethodValue;
  extraFields: ExtraField[];
}

export interface AddressForm {
  code: string;
  fullAddress: string;
  city: string;
  postcode: string;
  countryIsoCode: string;
  latitude: number | null;
  longitude: number | null;
  placeId: string;
}

// ────────────────────────────────────────────────────────────────────────────
// Form defaults
// ────────────────────────────────────────────────────────────────────────────

export const EMPTY_ADDRESS_FORM: AddressForm = {
  code: '',
  fullAddress: '',
  city: '',
  postcode: '',
  countryIsoCode: 'IE',
  latitude: null,
  longitude: null,
  placeId: '',
};

// ────────────────────────────────────────────────────────────────────────────
// Server DTO → form state
// ────────────────────────────────────────────────────────────────────────────

export function parseExtraFields(raw: unknown): ExtraField[] {
  if (raw == null) return [];
  let text: string;
  if (typeof raw === 'object') {
    const nested = (raw as { extraFields?: unknown }).extraFields;
    text = typeof nested === 'string' ? nested : '';
  } else if (typeof raw === 'string') {
    text = raw;
  } else {
    return [];
  }
  if (!text.trim()) return [];
  try {
    const parsed = JSON.parse(text) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (x): x is ExtraField =>
        typeof x === 'object' &&
        x !== null &&
        typeof (x as ExtraField).fieldLabel === 'string' &&
        typeof (x as ExtraField).fieldValue === 'string' &&
        ['text', 'number', 'date', 'boolean'].includes((x as ExtraField).fieldType as string),
    );
  } catch {
    return [];
  }
}

export function toOrgForm(org: Organisation): OrganisationForm {
  return {
    name: org.name ?? '',
    invoicingName: org.invoicingName ?? '',
    networkId: org.networkId ?? '',
    taxNumber: org.taxNumber ?? '',
    registrationNumber: org.registrationNumber ?? '',
    operationalTimeZone: org.operationalTimeZone ?? 'Europe/Dublin',
    languageCode: org.languageCode ?? 'en',
    timePattern: org.timePattern ?? 'HH:mm',
    datePattern: org.datePattern ?? 'dd/MM/yyyy',
    currencyIsoCode: org.currencyIsoCode ?? 'EUR',
    accountingSystem: org.accountingSystem ?? 'None',
    vatRoundingMethod: org.vatRoundingMethod ?? 'Total',
    extraFields: parseExtraFields(org.extraFields),
  };
}

export function toAddressForm(address: Address): AddressForm {
  return {
    code: address.code ?? '',
    fullAddress: address.fullAddress ?? '',
    city: address.city ?? '',
    postcode: address.postcode ?? '',
    countryIsoCode: address.countryIsoCode ?? '',
    latitude: address.latitude ?? null,
    longitude: address.longitude ?? null,
    placeId: address.placeId ?? '',
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Form state → wire request
// ────────────────────────────────────────────────────────────────────────────

export function toUpdateOrganisationRequest(
  org: Organisation,
  form: OrganisationForm,
  mainOfficeAddressId: string | null = org.mainOfficeAddressId ?? null,
): UpdateOrganisationRequest {
  return {
    organisationId: org.id,
    name: form.name.trim(),
    invoicingName: form.invoicingName.trim(),
    taxNumber: form.taxNumber.trim() || null,
    registrationNumber: form.registrationNumber.trim() || null,

    // Not editable here — Check does not invoice, so it has no payment-terms
    // form. Passed straight through from the loaded organisation: the update is
    // a whole-object PUT, so omitting them would reset the house terms every
    // time somebody saved the organisation from this app.
    defaultPaymentTermDays: org.defaultPaymentTermDays,
    defaultPaymentTermBasis: org.defaultPaymentTermBasis,

    operationalTimeZone: form.operationalTimeZone,
    languageCode: form.languageCode,
    currencyIsoCode: form.currencyIsoCode,
    countryIsoCode: org.countryIsoCode,
    timePattern: form.timePattern,
    datePattern: form.datePattern,

    // Echoed unchanged — the Check page doesn't surface these, but the PUT
    // is a full replace so we must not blank them out.
    additionalCurrencyIsoCodes: org.additionalCurrencyIsoCodes ?? null,
    extraFields: form.extraFields.length > 0 ? JSON.stringify(form.extraFields) : null,
    accountingSystem: form.accountingSystem,
    vatRoundingMethod: form.vatRoundingMethod,

    mainOfficeAddressId,
  };
}

/**
 * Build the POST body for `/addresses` from the address form. Used by the
 * Organisation page's "add main office address" flow. Server-side defaults
 * (status=Active, addressType=Primary) are spelt out explicitly here so the
 * created address always lines up with the org's `mainOfficeAddressId`
 * pointer.
 */
export function toCreateAddressRequest(
  organisationId: string,
  form: AddressForm,
): CreateAddressRequest {
  return {
    organisationId,
    addressType: 'Primary',
    name: 'Main Office',
    code: form.code.trim() || null,
    fullAddress: form.fullAddress.trim(),
    city: form.city.trim() || null,
    postcode: form.postcode.trim() || null,
    countryIsoCode: form.countryIsoCode.trim().toUpperCase(),
    latitude: form.latitude,
    longitude: form.longitude,
    placeId: form.placeId.trim(),
    status: 'Active',
  };
}

/**
 * Build the POST body for `/sites`. The Organisation page always seeds a
 * Site of `Type=Site` alongside the main office Address — every Address row
 * in the directory belongs to exactly one Site, so creating an address
 * without a paired Site leaves orphan data the rest of the suite can't
 * point at.
 */
export function toCreateSiteRequest(organisationId: string, addressId: string): CreateSiteRequest {
  return {
    organisationId,
    name: 'Main Office',
    type: 'Site',
    addressId,
    status: 'Active',
  };
}

export function toUpdateAddressRequest(address: Address, form: AddressForm): UpdateAddressRequest {
  return {
    addressId: address.id,
    addressType: address.addressType,
    name: address.name,
    // Code is editable from the OrganisationPage's AddressInlineEditor —
    // pull it from the form rather than echoing the server value, otherwise
    // operator edits get silently dropped.
    code: form.code.trim() || null,
    fullAddress: form.fullAddress.trim(),
    city: form.city.trim() || null,
    postcode: form.postcode.trim() || null,
    countryIsoCode: form.countryIsoCode.trim().toUpperCase(),
    latitude: form.latitude,
    longitude: form.longitude,
    placeId: form.placeId.trim(),
    status: address.status,
    serviceTags: address.serviceTags ?? null,
  };
}
